import {
  TextField,
  ReferenceField,
  FunctionField,
  DateField,
  useRecordContext,
  useCreatePath,
  useListContext,
} from "react-admin";
import { Button, Box, Typography, Chip } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { Link } from "react-router-dom";
import { EmptyState } from "../components/EmptyState";
import {
  AppList,
  AppListDefaultActions,
  AppListFilterCard,
  AppDatagrid,
  AppRowActions,
  SearchFilterInput,
  ReferenceFilterInput,
  SelectArrayFilterInput,
  DateFilterInput,
  ListEditButton,
} from "@/adapters/inbound/layout";
import type { JobRaRecord } from "../../react_admin/Mappers";
import { Link as MuiLink } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const JobReportButton = () => {
  const record = useRecordContext<any>();
  const createPath = useCreatePath();
  if (!record) return null;

  const to = createPath({
    resource: "jobs",
    id: record.id,
    type: "show",
  }).replace(/\/show$/, "/report");

  return (
    <Button
      component={Link}
      to={to}
      onClick={(e) => e.stopPropagation()}
      size="small"
      aria-label="Bericht"
    >
      <AssessmentIcon fontSize="small" />
    </Button>
  );
};

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const re = new RegExp(`(${escapeRegExp(q)})`, "ig");
  const parts = text.split(re);

  return (
    <>
      {parts.map((p, i) =>
        re.test(p) ? (
          <mark key={i} style={{ padding: "0 2px", borderRadius: 3 }}>
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

function makeSnippet(text: string, query: string, maxLen = 80) {
  const q = query.trim();
  if (!q || !text) return null;

  const lower = text.toLowerCase();
  const idx = lower.indexOf(q.toLowerCase());
  if (idx === -1) return null;

  const half = Math.floor(maxLen / 2);
  const start = Math.max(0, idx - half);
  const end = Math.min(text.length, idx + q.length + half);

  const prefix = start > 0 ? "…" : "";
  const suffix = end < text.length ? "…" : "";
  return `${prefix}${text.slice(start, end)}${suffix}`;
}

function JobTitleWithSearchReason() {
  const record = useRecordContext<any>();
  const { filterValues } = useListContext();

  const q = String(filterValues?.q ?? "").trim();
  if (!record) return null;

  // If there is no active search query, just show title.
  if (!q) return <span>{record.title}</span>;

  const title = String(record.title ?? "");
  const desc = String(record.description ?? "");
  const notes = String(record.notes ?? "");

  const titleHit = title.toLowerCase().includes(q.toLowerCase());
  const descSnippet = !titleHit ? makeSnippet(desc, q) : null;
  const notesSnippet = !titleHit && !descSnippet ? makeSnippet(notes, q) : null;

  return (
    <Box>
      <Typography variant="body2" component="div">
        <Highlight text={title} query={q} />
      </Typography>

      {/* Only show a reason line if the match likely came from secondary fields */}
      {descSnippet && (
        <Typography variant="caption" color="text.secondary" component="div">
          Treffer in Beschreibung: <Highlight text={descSnippet} query={q} />
        </Typography>
      )}

      {notesSnippet && (
        <Typography variant="caption" color="text.secondary" component="div">
          Treffer in Notiz: <Highlight text={notesSnippet} query={q} />
        </Typography>
      )}
    </Box>
  );
}

const jobStateChoices = [
  { id: "OPEN", name: "Offen" },
  { id: "IN_PROGRESS", name: "In Arbeit" },
  { id: "INTERRUPTED", name: "Unterbrochen" },
  { id: "CLOSED_FOR_DAY", name: "Für heute beendet" },
  { id: "COMPLETED", name: "Abgeschlossen" },
];

const jobStateMeta: Record<
  string,
  {
    label: string;
    color: "default" | "primary" | "success" | "warning" | "info";
  }
> = {
  OPEN: { label: "Offen", color: "default" },
  IN_PROGRESS: { label: "In Arbeit", color: "primary" },
  INTERRUPTED: { label: "Unterbrochen", color: "warning" },
  CLOSED_FOR_DAY: { label: "Für heute beendet", color: "info" },
  COMPLETED: { label: "Abgeschlossen", color: "success" },
};

function JobStateField() {
  const record = useRecordContext<JobRaRecord>();
  const state = record?.jobState ?? "";
  const meta = jobStateMeta[state];

  if (!meta) {
    return <Chip label="—" size="small" variant="outlined" />;
  }

  return (
    <Chip
      label={meta.label}
      size="small"
      color={meta.color}
      variant="outlined"
    />
  );
}

function AssignedPersonLink() {
  const record = useRecordContext<any>();
  const createPath = useCreatePath();

  if (!record) return <>—</>;

  const to = createPath({
    resource: "persons",
    id: record.id,
    type: "show",
  });

  return (
    <MuiLink
      component={RouterLink}
      to={to}
      underline="hover"
      color="primary"
      onClick={(e) => e.stopPropagation()}
    >
      {record.displayName || "—"}
    </MuiLink>
  );
}

export const JobList = () => (
  <AppList
    perPage={25}
    sort={{ field: "createdAt", order: "DESC" }}
    empty={
      <EmptyState
        title="Noch keine Aufträge"
        hint="Legen Sie den ersten Auftrag an, um Kunden, Einsatzorte und Arbeiten zentral zu verwalten."
      />
    }
  >
    <AppListDefaultActions />

    <AppListFilterCard
      search={<SearchFilterInput source="q" label="Suche" alwaysOn />}
      filters={
        <>
          <SelectArrayFilterInput
            source="jobState"
            label="Status"
            choices={jobStateChoices}
          />

          <DateFilterInput source="plannedFrom" label="Start ab" />
          <DateFilterInput source="plannedTo" label="Start bis" />

          <ReferenceFilterInput
            source="constructionSiteId"
            reference="constructionSites"
            label="Einsatzort"
            autocompleteProps={{
              optionText: "label",
            }}
          />

          <ReferenceFilterInput
            source="assignedPersonId"
            reference="assignees"
            label="Zugewiesen"
            autocompleteProps={{
              optionText: "displayName",
            }}
          />
        </>
      }
    />

    <AppDatagrid rowClick="show">
      <FunctionField
        label="Titel"
        render={() => <JobTitleWithSearchReason />}
      />

      <ReferenceField
        source="constructionSiteId"
        reference="constructionSites"
        link="show"
        label="Einsatzort"
      >
        <TextField source="label" emptyText="—" />
      </ReferenceField>

      <ReferenceField
        source="assignedPersonId"
        reference="assignees"
        link={false}
        label="Zugewiesen"
      >
        <AssignedPersonLink />
      </ReferenceField>

      <DateField
        source="plannedStartAt"
        label="Start"
        locales="de-DE"
        options={{
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }}
        emptyText="—"
      />

      <FunctionField
        label="Status"
        render={() => <JobStateField />}
        sortable={false}
      />

      <FunctionField
        label={false}
        render={() => (
          <AppRowActions>
            <JobReportButton />
            <ListEditButton />
          </AppRowActions>
        )}
      />
    </AppDatagrid>
  </AppList>
);
